import { Component, OnDestroy } from '@angular/core';
import {
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { from, of, Subscription, timer } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { SecurityScope } from '../../auth/auth.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { useDispatcher } from '../../store.decorator';
import { SearchUserDialog } from '../../user/search-user.dialog';
import { Meeting } from '../core';

import { TranscriptImportHookService } from './hook.service';
import { WillImportContext } from './model/will-import-context.model';
import { listMeetings } from './state/list-meetings.action';
import { selectDateRange } from './state/select-date-range.action';
import { selectUser } from './state/select-user.action';
import { TranslationKeyMap } from './transcript-import-dialog.i18n';
import { TranscriptImportDialogService } from './transcript-import-dialog.service';

@Component({
    selector: 'cmps-teamscall-transcript-import-dialog',
    templateUrl: './transcript-import-dialog.component.html',
    host: {
        class: 'cmps-teamscall-transcript-import-dialog',
    },
})
@TakeUntilDestroy()
export class TranscriptImportDialogComponent implements OnDestroy {
    /** @private */
    _i18n = TranslationKeyMap;

    private _importSub: Subscription | null;

    constructor(
        private readonly _translateService: TranslateService,
        /** @private */ readonly _service: TranscriptImportDialogService,
        private readonly _userSearchDialog: SearchUserDialog,
        private readonly _hookService: TranscriptImportHookService,
        private readonly _snackBar: CmpsSnackBar,
    ) {}

    ngOnDestroy(): void {}

    /** @private */
    _onHasValidTokenChange(hasValidToken: boolean): void {
        if (!hasValidToken) {
            console.log('no valid msft token');
            return;
        }
        this._run();
    }

    /** @private */
    _onUserInputClick(): void {
        this._userSearchDialog.openUsingNgbModal({
            options: {
                windowClass:
                    'cmps-search-user-modal cmps-teamscall-transcript-import-dialog--user-search',
            },
            userId: this._service.state.selectedUserId,
            scope: SecurityScope.COACHING /* TODO revise */,
            close: (user) => {
                // delay because of propagating events
                timer(1000)
                    .pipe(
                        tap(() => {
                            this._service.waitingForUserSearch = false;
                        }),
                        takeUntilDestroy(this),
                    )
                    .subscribe();

                if (user == null) {
                    return;
                }

                useDispatcher(
                    this._service,
                    selectUser({
                        userId: user.id,
                        name: `${user.firstName} ${user.lastName}`,
                    }),
                );
            },
        });
        this._service.waitingForUserSearch = true;
    }

    /** @private */
    _onDateRangeChange(dateRange: { from: Date; to: Date }): void {
        useDispatcher(this._service, selectDateRange(dateRange));
    }

    /** @private */
    _onShowResultsClick(): void {
        this._run();
    }

    /** @private */
    _onImportClick(meeting: Meeting): void {
        this._importSub?.unsubscribe();
        const ctx: WillImportContext = {
            userId: this._service.state.currentRequestSelectedUserId,
            context: {},
        };
        const proms = this._hookService.willImport(ctx);
        this._importSub = from(Promise.all<boolean>(proms))
            .pipe(
                catchError(() => of<boolean[]>([])),
                tap((ret) => {
                    this._service.waitingForImport = false;

                    if (ret.some((e) => !e)) {
                        return;
                    }

                    void this._service.callStatusService.socketService.importTranscript(
                        {
                            msftMeetingId: meeting.msftMeetingId,
                            context: JSON.stringify(ctx.context),
                        },
                    );

                    const data: CmpsTextOnlySnackBarOptions = {
                        text: this._translateService.instant(
                            TranslationKeyMap._11263_TRANSCRIPT_IS_BEING_IMPORTED,
                        ),
                    };

                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 5000,
                        placement: 'bottom-left',
                        data,
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
        this._service.waitingForImport = true;
    }

    private _run(): void {
        const token = `list-meetings-${String(Math.random())}`;
        useDispatcher(this._service, listMeetings({ token }));

        /* TODO timeout */
        /* TODO cancellation */
        void this._service.callStatusService.socketService.listMeetings({
            token,
            meetingIds: null,
            callHandleIds: null,
            msftChatThreadIds: null,
            msftCallIds: null,
            participantIds: [this._service.state.selectedUserId],
            startDate: this._service.state.dateRange.from,
            endDate: this._service.state.dateRange.to,
        });
    }
}
